import * as React from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
} from '@mui/material';
import EuroIcon from '@mui/icons-material/Euro';
import BuildIcon from '@mui/icons-material/Build';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { withTranslation } from 'react-i18next';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import MetricCard from '../components/MetricCard';
import ExportButtons from '../components/ExportButtons';
import { useSummaryReport } from '../hooks/useReportsApi';

const STATUS_COLORS = {
  planned: '#64748b',
  in_progress: '#3b82f6',
  completed: '#10b981',
  awaiting_pickup: '#f59e0b',
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(value);
};

function SummaryTab({ t, dateParams }) {
  const { data, loading, error } = useSummaryReport(dateParams);

  if (error) {
    return <Alert severity="error">{t('reports.error')}: {error}</Alert>;
  }

  return (
    <Box>
      {/* Export Buttons */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <ExportButtons reportType="summary" params={dateParams} />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={EuroIcon}
            label={t('reports.summary.revenue')}
            value={data?.revenue?.value}
            changePercent={data?.revenue?.change_percent}
            changeType={data?.revenue?.change_type}
            loading={loading}
            format="currency"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={BuildIcon}
            label={t('reports.summary.workOrders')}
            value={data?.work_orders?.value}
            changePercent={data?.work_orders?.change_percent}
            changeType={data?.work_orders?.change_type}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={PeopleIcon}
            label={t('reports.summary.activeCustomers')}
            value={data?.active_customers?.value}
            changePercent={data?.active_customers?.change_percent}
            changeType={data?.active_customers?.change_type}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            icon={ReceiptIcon}
            label={t('reports.summary.avgInvoice')}
            value={data?.average_invoice?.value}
            changePercent={data?.average_invoice?.change_percent}
            changeType={data?.average_invoice?.change_type}
            loading={loading}
            format="currency"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Revenue Trend */}
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.summary.revenueTrend')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data?.revenue_trend || []}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period_label" />
                  <YAxis tickFormatter={(value) => `${(value / 1000).toFixed(0)}k €`} />
                  <Tooltip
                    formatter={(value) => [formatCurrency(value), t('reports.summary.revenue')]}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    stroke="#3182ce"
                    strokeWidth={2}
                    dot={{ fill: '#3182ce' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid>

        {/* Work Order Status */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.summary.workOrderStatus')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={300} />
            ) : (data?.work_order_status || []).length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data.work_order_status}
                    cx="50%"
                    cy="45%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="status_display"
                  >
                    {data.work_order_status.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.status] || entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    verticalAlign="bottom"
                    formatter={(value, entry) => `${value} (${entry.payload?.count ?? 0})`}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">{t('reports.noData')}</Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Tables Row */}
      <Grid container spacing={3}>
        {/* Top Customers */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.summary.topCustomers')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('reports.summary.customer')}</TableCell>
                      <TableCell align="right">{t('reports.summary.orders')}</TableCell>
                      <TableCell align="right">{t('reports.summary.revenue')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.top_customers || []).map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>{customer.name}</TableCell>
                        <TableCell align="right">{customer.orders}</TableCell>
                        <TableCell align="right">{formatCurrency(customer.revenue)}</TableCell>
                      </TableRow>
                    ))}
                    {(!data?.top_customers || data.top_customers.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                          {t('reports.noData')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>

        {/* Employee Productivity */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {t('reports.summary.employeeProductivity')}
            </Typography>
            {loading ? (
              <Skeleton variant="rectangular" height={200} />
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('reports.summary.employee')}</TableCell>
                      <TableCell align="right">{t('reports.summary.workOrders')}</TableCell>
                      <TableCell align="right">{t('reports.summary.revenue')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.employee_productivity || []).map((employee) => (
                      <TableRow key={employee.id}>
                        <TableCell>{employee.name}</TableCell>
                        <TableCell align="right">{employee.work_orders}</TableCell>
                        <TableCell align="right">{formatCurrency(employee.revenue)}</TableCell>
                      </TableRow>
                    ))}
                    {(!data?.employee_productivity || data.employee_productivity.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                          {t('reports.noData')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default withTranslation()(SummaryTab);
